import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import ZoomableImage from "@/components/site/ZoomableImage";

// 13 gallery slots — real photos plug in as they arrive.
// To add more photos: drop files in /app/frontend/public/images/gallery/
// and set src below (e.g. "/images/gallery/06.webp").
const IMAGES = [
  { id: 1, src: "/images/gallery/01.webp", alt: "Hol renovat cu ancadramente decorative — Aurel Calin Premium" },
  { id: 2, src: "/images/gallery/02.webp", alt: "Hidroizolație baie — Aurel Calin Premium" },
  { id: 3, src: "/images/gallery/03.webp", alt: "Demolare și pregătire pentru finisaje noi — Aurel Calin Premium" },
  { id: 4, src: "/images/gallery/04.webp", alt: "Montaj gresie porțelanată travertin pe balcon — Aurel Calin Premium" },
  { id: 5, src: "/images/gallery/05.webp", alt: "Aplicare hidroizolație și bandă de etanșare — Aurel Calin Premium" },
  { id: 6, src: "/images/gallery/06.jpg", alt: "Amenajare exterioară cu pavaj în două tonuri — Aurel Calin Premium" },
  { id: 7, src: "/images/gallery/07.jpg", alt: "Scări exterioare placate cu piatră naturală — Aurel Calin Premium" },
  { id: 8, src: "/images/gallery/08.jpg", alt: "Living cu tavan casetat din gipscarton și pardoseală marmură — Aurel Calin Premium" },
  { id: 9, src: "/images/gallery/09.jpg", alt: "Structură de beton armat pentru planșeu — Aurel Calin Premium" },
  { id: 10, src: "/images/gallery/10.webp", alt: "Baie placată cu gresie decorativă și cadă cu hidromasaj — Aurel Calin Premium" },
  { id: 11, src: "/images/gallery/11.webp", alt: "Fațadă în lucru cu termoizolație și schelă — Aurel Calin Premium" },
  { id: 12, src: "/images/gallery/12.webp", alt: "Structură metalică pentru tavan fals din gipscarton — Aurel Calin Premium" },
  { id: 13, src: "/images/gallery/13.webp", alt: "Bloc rezidențial finalizat cu tencuială și balcoane — Aurel Calin Premium" },
  { id: 14, src: "/images/gallery/14.webp", alt: "Cameră pregătită pentru finisaje cu tâmplărie PVC montată — Aurel Calin Premium" },
  { id: 15, src: "/images/gallery/15.jpg", alt: "Izolație termică cu vată minerală în șarpantă — Aurel Calin Premium" },
  { id: 16, src: "/images/gallery/16.webp", alt: "Baie în lucru cu protecții aplicate pe pereți — Aurel Calin Premium" },
];

export default function Gallery() {
  const [openIdx, setOpenIdx] = useState(null);

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length)),
    []
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % IMAGES.length)),
    []
  );

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx, close, prev, next]);

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[var(--bg-elevated)]"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 reveal">
          <div className="max-w-xl">
            <div className="eyebrow mb-5">
              <span>Portofoliu</span>
            </div>
            <h2 className="font-display text-[var(--text-primary)] text-4xl md:text-5xl leading-[1.08] font-medium">
              Lucrări recente,
              <span className="text-brand-blue italic"> finisaje premium</span>.
            </h2>
          </div>
          <p className="text-[var(--text-secondary)] md:text-right max-w-md">
            O selecție din cele peste 400 de proiecte livrate în Constanța și
            împrejurimi. Apasă pe orice imagine pentru a o mări.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {IMAGES.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => setOpenIdx(idx)}
              data-testid={`gallery-item-${idx}`}
              className="gallery-item reveal focus-ring"
              style={{ transitionDelay: `${(idx % 4) * 40}ms` }}
              aria-label={`Deschide imaginea ${idx + 1}`}
            >
              {img.src ? (
                <img src={img.src} alt={img.alt} loading="lazy" />
              ) : (
                <div className="placeholder">
                  <ImageIcon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={1.4} />
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] font-medium">
                    Foto {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {openIdx !== null && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={close}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            data-testid="lightbox-close"
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors focus-ring"
            aria-label="Închide"
          >
            <X className="h-5 w-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            data-testid="lightbox-prev"
            className="absolute left-4 md:left-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors focus-ring"
            aria-label="Imaginea anterioară"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            data-testid="lightbox-next"
            className="absolute right-4 md:right-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors focus-ring"
            aria-label="Imaginea următoare"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-5xl w-full aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 bg-[var(--bg-card)] flex items-center justify-center"
          >
            {IMAGES[openIdx].src ? (
              <ZoomableImage
                src={IMAGES[openIdx].src}
                alt={IMAGES[openIdx].alt}
              />
            ) : (
              <div className="text-center text-[var(--text-muted)] flex flex-col items-center gap-3">
                <ImageIcon className="h-14 w-14" strokeWidth={1.2} />
                <div className="uppercase tracking-[0.25em] text-xs">
                  Foto {String(openIdx + 1).padStart(2, "0")} · în curând
                </div>
              </div>
            )}
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
            {openIdx + 1} / {IMAGES.length}
          </div>
        </div>
      )}
    </section>
  );
}
